// React Utils
import { Link, useLocation } from "react-router-dom";
// Packages components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
// Assets
import bckWoop from "@/assets/Logo-AppWhoop.webp"; // Placeholder image for lazy loading

// Utils
import { scrollToTop, formatFechaHoraEvento } from "@/lib/utilsUI";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";

/**
 * TicketCardReferralLink
 * Componente para mostrar un link de referido en formato de tarjeta de ticket
 * 
 * @param {Object} props
 * @param {Object} props.referralLink - Datos del link de referido
 * @param {string} props.referralLink.uid - UID del link
 * @param {string} props.referralLink.code - Código de referido
 * @param {string} props.referralLink.url - URL completa del link
 * @param {Object} props.referralLink.evento - Información del evento
 * @param {string} props.referralLink.evento.uid - UID del evento
 * @param {string} props.referralLink.evento.nombre - Nombre del evento
 * @param {string} props.referralLink.evento.fechaHoraInicio - Fecha de inicio
 * @param {string} props.referralLink.evento.fechaHoraFin - Fecha de fin
 * @param {string} props.referralLink.evento.img - URL de la imagen del evento
 * @param {string} props.referralLink.evento.estado - Estado del evento
 * @param {Object} props.referralLink.stats - Estadísticas del link
 * @param {number} props.referralLink.stats.totalClicks - Total de clicks
 * @param {number} props.referralLink.stats.totalConversions - Total de conversiones
 * @param {number} props.referralLink.stats.totalComisionAmbassador - Total de comisión ganada por el embajador
 * @param {number} props.referralLink.stats.totalSales - Total de ventas generadas
 * @param {string} props.referralLink.stats.conversionRate - Tasa de conversión
 */
export default function TicketCardReferralLink({ referralLink }) {
  console.log("Rendering TicketCardReferralLink with referralLink:", referralLink);
  
  // Detectar si estamos en vista pública de perfil
  const location = useLocation();
  const isPublicView = location.pathname.startsWith('/usuario/');
  
  // Construir la URL del evento con o sin referral code
  const eventoUrl = isPublicView 
    ? `/evento/${referralLink.evento?.uid}?ref=${referralLink.code}`
    : `/evento/${referralLink.evento?.uid}`;
  
  // Formatear fecha de inicio del evento usando UTC
  const formatDatetime = referralLink?.evento?.fechaHoraInicio
    ? formatFechaHoraEvento(
        parseUTCDate(
          referralLink.evento.fechaHoraInicio, 
          referralLink.evento.zonaHoraria || DEFAULT_TIMEZONE
        )?.toJSDate(),
        referralLink.evento.zonaHoraria || DEFAULT_TIMEZONE
      )
    : null;

  // Determinar el estado del evento basado en 'published'
  // published: 0=No publicado, 1=Activo, 2=Finalizado, 3=Cancelado
  const published = referralLink.evento?.published;
  const isActive = published === 1;
  const enabled = referralLink?.enabled;
  const isCanceled = published === 3;
  const isFinalized = published === 2;
  console.log("[enabled]: ", referralLink?.enabled);
  
  // Determinar el badge a mostrar basado en 'published'
  const getBadgeInfo = () => {
    if (isCanceled) {
      return { text: 'Cancelado', bgColor: 'bg-red-600', textColor: 'text-white' };
    }
    if (isFinalized) {
      return { text: 'Finalizado', bgColor: 'bg-aw-bluegray', textColor: 'text-white'};
    }
    if (published === 0) {
      return { text: 'No Publicado', bgColor: 'bg-gray-500', textColor: 'text-white' };
    }
    
    // Para eventos activos (published === 1), verificar si ya finalizó por fecha
    if (isActive) {
      const now = new Date();
      const fechaFin = referralLink.evento?.fechaHoraFin 
        ? parseUTCDate(referralLink.evento.fechaHoraFin, referralLink.evento.zonaHoraria || DEFAULT_TIMEZONE)?.toJSDate()
        : null;
      
      if (fechaFin && fechaFin < now) {
        return { text: 'Finalizado', bgColor: 'bg-aw-bluegray', textColor: 'text-white'};
      }
    }
    if (enabled === 0) {
      return { text: 'Desactivado', bgColor: 'bg-yellow-500', textColor: 'text-black'};
    }
    
    return { text: 'Activo', bgColor: 'bg-aw-green', textColor: 'text-black'};
  };

  const badgeInfo = getBadgeInfo();

  // Determinar color del botón según estado
  const getButtonColor = () => {
    if (isCanceled || published === 0) return 'bg-gray-500';
    return 'bg-aw-green';
  };

  return (
    <div className="min-w-[200px] max-w-[200px] bg-aw-bluedark rounded-b-xl shadow-lg overflow-hidden relative">
      {/* Badge de estado */}
      <div className={`absolute top-2 right-2 z-10 ${badgeInfo.bgColor} ${badgeInfo.textColor} text-xs px-2 py-1 rounded-full font-semibold`}>
        {badgeInfo.text}
      </div>
      
      {/* Badge de código de referido */}
      <div className="absolute top-2 left-2 z-10 bg-aw-bluegray text-aw-green text-xs px-2 py-1 rounded-full font-bold border border-aw-green">
        {referralLink.code}
      </div>
      
      {/* Imagen del Evento */}
      <Link
        to={eventoUrl}
        onClick={scrollToTop}
        className="h-52 block overflow-hidden relative border-2 border-aw-green bg-aw-bluegray"
      >
        <img
          src={bckWoop}
          alt="Placeholder"
          className="absolute inset-0 w-2/3 h-2/3 m-auto top-0 left-0 object-cover z-0"
        />
        <LazyLoadImage
          src={referralLink.evento?.img || bckWoop}
          placeholderSrc={bckWoop}
          alt={referralLink.evento?.nombre}
          height={"100%"}
          width={"100%"}
          effect="blur"
          className="w-full h-full object-cover z-50"
          loading="lazy"
        />
      </Link>
      
      <div className="px-2 py-1 flex flex-col justify-between h-[150px] text-center">
        <span className="text-tiny text-white capitalize">
          {formatDatetime || "Fecha no disponible"}
        </span>
        <h3 className="font-bold text-sm text-white line-clamp-2">
          {referralLink.evento?.nombre}
        </h3>
        
        {/* Estadísticas del link - Solo visible en vista privada */}
        {!isPublicView && (
          <>
            <div className="flex justify-around text-xs text-white mt-1">
              <div className="flex flex-col">
                <span className="font-bold text-aw-green">{referralLink.stats?.totalClicks}</span>
                <span className="text-tiny text-gray-400">Clicks</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-aw-green">{referralLink.stats?.totalConversions}</span>
                <span className="text-tiny text-gray-400">Ventas</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-aw-green">{referralLink.stats?.conversionRate}</span>
                <span className="text-tiny text-gray-400">Conv.</span>
              </div>
            </div>
            
            {/* Total de comisión ganada */}
            {referralLink.stats?.totalComisionAmbassador > 0 && (
              <div className="text-white text-xs font-semibold mt-1">
                Comisiones: <span className="text-aw-green">${referralLink.stats?.totalComisionAmbassador?.toLocaleString('es-CL')}</span>
              </div>
            )}
          </>
        )}
        
        {/* En vista pública, mostrar ubicación */}
        {isPublicView && (
          <p className="text-xs text-gray-500 line-clamp-1 mt-2">
            <FontAwesomeIcon
              icon="fa-solid fa-map-pin"
              className="text-red-700 mr-1"
            />
            {referralLink.evento?.direccion || "Ubicación no disponible"}
          </p>
        )}
      </div>
      
      {/* Botón Ver Evento */}
      <Link
        to={eventoUrl}
        onClick={scrollToTop}
        className={`w-full h-9 flex items-center justify-center text-black font-semibold text-sm mt-2 py-1 hover:scale-110 transition duration-300 ${getButtonColor()}`}
      >
        Ver Evento
        <FontAwesomeIcon
          icon="fa-solid fa-calendar-days"
          className="ml-2"
        />
      </Link>
    </div>
  );
}
